import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  InputAdornment,
  Chip,
  Typography
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import styled from "styled-components";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import { useTranslation } from "react-i18next";
import Group from "../../waybee-ui/Group";
import BackButton from "../BackButton";
import Button from "../../waybee-ui/Button";
import JobInProgressLoading from "../JobInProgressLoading";
import TeacherService from "../../services/TeacherService";
import useJobQueueWaiting from "../../utils/useJobQueueWaiting";
import {
  parseAdvancedConfig,
  withStageFields,
  filterOccurrences
} from "../../utils/occurrenceAdvanced";
import AdvancedOccurrencesLayout from "./AdvancedOccurrencesLayout";
import OccurrenceTableList from "./OccurrenceTableList";
import OccurrenceDialog from "./OccurrenceDialog";
import NewOccurrenceDialog from "./NewOccurrenceDialog";
import OccurrenceFilterControl from "./OccurrenceFilterControl";

const YEARS_BACK = 5;

const LoadContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 8px;
`;

const OccurrenceList = styled(Group)`
  padding: 0 30px 12px 30px;
`;

const HeaderCard = styled(Group)`
  padding: 0 30px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 0;
`;

const ToolbarFilters = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1;
`;

const SearchField = styled(TextField)`
  flex: 1;
  min-width: 240px;
  max-width: 360px;
`;

const CountChip = styled(Chip)`
  font-weight: 600;
`;

const buildYearOptions = currentYear =>
  Array.from({ length: YEARS_BACK + 1 }, (_, index) => currentYear - index);

const loadStages = async (classId, year) => {
  if (!classId) {
    return [];
  }
  try {
    return (await TeacherService.getOccurrenceStages(classId, year)) || [];
  } catch (err) {
    return [];
  }
};

export default function OccurrencesPage({
  classId,
  backTo,
  header,
  filters,
  fetchOccurrences,
  occurrenceFilter,
  matchesJob,
  advanced,
  searchScope,
  canView,
  canCreate,
  addLabel,
  studentId,
  studentName,
  students,
  classes,
  allowedGroupIds
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const yearOptions = useMemo(
    () => buildYearOptions(new Date().getFullYear()),
    []
  );
  const [year, setYear] = useState(yearOptions[0]);
  const [occurrences, setOccurrences] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [advancedConfig, setAdvancedConfig] = useState(() =>
    parseAdvancedConfig({})
  );
  const [selectedOccurrence, setSelectedOccurrence] = useState(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState(false);

  const baseOccurrences = useMemo(
    () =>
      occurrences && occurrenceFilter
        ? occurrences.filter(occurrenceFilter)
        : occurrences,
    [occurrences, occurrenceFilter]
  );

  const visibleOccurrences = useMemo(
    () =>
      advanced ? filterOccurrences(baseOccurrences, search) : baseOccurrences,
    [advanced, baseOccurrences, search]
  );

  const reload = useCallback(() => setReloadToken(token => token + 1), []);
  const { waiting, startWaiting } = useJobQueueWaiting(matchesJob, reload);

  const dateLocale = (i18n.language || "pt-BR").startsWith("en")
    ? "en-US"
    : "pt-BR";

  useEffect(() => {
    if (!advanced) {
      return;
    }
    TeacherService.getOccurrenceAdvancedConfig()
      .then(config => setAdvancedConfig(parseAdvancedConfig(config)))
      .catch(() => setAdvancedConfig(parseAdvancedConfig({})));
  }, [advanced]);

  useEffect(() => {
    let active = true;
    setOccurrences(null);
    setLoadError(false);

    Promise.all([fetchOccurrences(year), loadStages(classId, year)])
      .then(([list, stages]) => {
        if (active) {
          setOccurrences(withStageFields(list || [], stages));
        }
      })
      .catch(() => {
        if (active) {
          setLoadError(true);
        }
      });

    return () => {
      active = false;
    };
  }, [classId, fetchOccurrences, year, reloadToken]);

  const handleSelect = occurrence => {
    setSelectedOccurrence({
      ...occurrence,
      stage: occurrence.stage || occurrence.stageShortName || occurrence.idStage
    });
  };

  const handleDialogClose = hasChanged => {
    setSelectedOccurrence(null);
    setCreating(false);
    if (hasChanged === true) {
      startWaiting();
    }
  };

  const renderList = () => {
    if (waiting) {
      return <JobInProgressLoading message={t("occurrences.jobInProgress")} />;
    }
    if (loadError) {
      return (
        <LoadContainer>
          <Typography gutterBottom>{t("occurrences.loadError")}</Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={reload}
          >
            {t("occurrences.retry")}
          </Button>
        </LoadContainer>
      );
    }
    if (!occurrences) {
      return (
        <LoadContainer>
          <CircularProgress />
        </LoadContainer>
      );
    }
    const emptyMessage =
      advanced && search.trim() && baseOccurrences.length
        ? t("occurrences.advanced.emptySearch")
        : t("occurrences.student.emptyYear");
    if (advanced) {
      return (
        <AdvancedOccurrencesLayout
          occurrences={visibleOccurrences}
          config={advancedConfig}
          primaryColor={theme.palette.primary.main}
          dateLocale={dateLocale}
          onSelect={canView ? handleSelect : undefined}
          emptyMessage={emptyMessage}
        />
      );
    }
    return (
      <OccurrenceTableList
        occurrences={visibleOccurrences}
        primaryColor={theme.palette.primary.main}
        dateLocale={dateLocale}
        onSelect={handleSelect}
        emptyMessage={emptyMessage}
      />
    );
  };

  return (
    <Grid container spacing={2}>
      <BackButton to={backTo} />
      <Grid item xs={12}>
        <HeaderCard>{header}</HeaderCard>
      </Grid>
      <Grid item xs={12}>
        <OccurrenceList>
          <Toolbar>
            <ToolbarFilters>
              <OccurrenceFilterControl>
                <InputLabel id="occurrence-year-label">
                  {t("occurrences.student.year")}
                </InputLabel>
                <Select
                  labelId="occurrence-year-label"
                  label={t("occurrences.student.year")}
                  value={year}
                  onChange={e => setYear(e.target.value)}
                >
                  {yearOptions.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </OccurrenceFilterControl>
              {filters}
              {advanced && (
                <SearchField
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder={t(
                    searchScope === "student"
                      ? "occurrences.advanced.searchPlaceholderProfile"
                      : "occurrences.advanced.searchPlaceholder"
                  )}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              )}
              {advanced && occurrences && (
                <CountChip
                  label={
                    search.trim()
                      ? t("occurrences.advanced.countFiltered", {
                          count: visibleOccurrences.length,
                          total: baseOccurrences.length
                        })
                      : t("occurrences.advanced.count", {
                          count: baseOccurrences.length
                        })
                  }
                  color="primary"
                  variant="outlined"
                />
              )}
            </ToolbarFilters>
            {canCreate && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setCreating(true)}
              >
                <AddIcon />
                {addLabel}
              </Button>
            )}
          </Toolbar>
          {renderList()}
        </OccurrenceList>
      </Grid>
      {selectedOccurrence && (
        <OccurrenceDialog
          open
          studentId={selectedOccurrence.studentId || studentId}
          classId={classId || undefined}
          handleClose={handleDialogClose}
          occurrence={selectedOccurrence}
        />
      )}
      {creating && (
        <NewOccurrenceDialog
          open
          studentId={studentId}
          studentName={studentName}
          students={students}
          classId={classId || undefined}
          classes={classes}
          rich={advanced}
          allowedGroupIds={allowedGroupIds}
          handleClose={handleDialogClose}
        />
      )}
    </Grid>
  );
}

OccurrencesPage.propTypes = {
  classId: PropTypes.string,
  backTo: PropTypes.string.isRequired,
  header: PropTypes.node.isRequired,
  filters: PropTypes.node,
  fetchOccurrences: PropTypes.func.isRequired,
  occurrenceFilter: PropTypes.func,
  matchesJob: PropTypes.func.isRequired,
  advanced: PropTypes.bool,
  searchScope: PropTypes.oneOf(["class", "student"]),
  canView: PropTypes.bool,
  canCreate: PropTypes.bool,
  addLabel: PropTypes.string.isRequired,
  studentId: PropTypes.string,
  studentName: PropTypes.string,
  students: PropTypes.arrayOf(PropTypes.shape()),
  classes: PropTypes.arrayOf(PropTypes.shape()),
  allowedGroupIds: PropTypes.arrayOf(PropTypes.string)
};

OccurrencesPage.defaultProps = {
  classId: null,
  filters: null,
  occurrenceFilter: null,
  advanced: false,
  searchScope: "class",
  canView: false,
  canCreate: false,
  studentId: undefined,
  studentName: undefined,
  students: undefined,
  classes: undefined,
  allowedGroupIds: null
};
